import { z } from "zod";
import { ZDateish, ZObjectIdString } from "./foundationSchema";
/* -------------------------------------------------------------------------- */
/*  Basics                                                                    */
/* -------------------------------------------------------------------------- */
export const ZIsoDateString = z.string().datetime();
export const ZReleaseType = z.union([z.literal("added"), z.literal("changed"), z.literal("fixed")]);
export const ZImpact = z.union([z.literal("low"), z.literal("medium"), z.literal("high")]);
export const ZArea = z.string().min(1);
/* -------------------------------------------------------------------------- */
/*  Media (Blob storage)                                                      */
/* -------------------------------------------------------------------------- */
export const ZMediaKind = z.union([z.literal("image"), z.literal("video")]);
export const ZBlobMedia = z.object({
    kind: ZMediaKind,
    blobPath: z.string().min(1),
    posterBlobPath: z.string().min(1).optional(),
    contentType: z.string().min(1).optional(),
    caption: z.string().min(1).optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    durationSec: z.number().positive().optional(),
});
export const ZModule = z.union([
    z.literal("CEILING"),
    z.literal("RFP"),
    z.literal("EXPLORE"),
    z.literal("ROOM_NUMBERING"),
]);
/* -------------------------------------------------------------------------- */
/*  Deploy note (internal)                                                    */
/* -------------------------------------------------------------------------- */
export const ZDeployChangeItem = z.object({
    _id: ZObjectIdString,
    userFacing: z.boolean().default(false),
    title: z.string().min(1),
    type: ZReleaseType,
    module: ZModule,
    area: ZArea,
    impact: ZImpact.optional(),
    text: z.string().min(1),
    media: z.array(ZBlobMedia).optional(),
});
export const ZDeployNote = z.object({
    _id: z.any().optional(),
    mergedAt: ZIsoDateString,
    gitSha: z.string().min(7).optional(),
    pr: z
        .object({
        id: z.number().int().positive().optional(),
        title: z.string().min(1).optional(),
    })
        .optional(),
    internalNote: z.string().min(1).optional(),
    changeItems: z.array(ZDeployChangeItem).default([]),
    publishedInReleaseId: ZObjectIdString.nullable().optional(),
});
/* -------------------------------------------------------------------------- */
/*  Release note (end-user)                                                   */
/* -------------------------------------------------------------------------- */
export const ZReleaseItem = z.object({
    type: ZReleaseType,
    area: ZArea,
    title: z.string().nullable().optional(),
    module: ZModule,
    impact: ZImpact.optional(),
    text: z.string().min(1),
    media: z
        .preprocess((v) => (v == null ? [] : v), z.array(ZBlobMedia))
        .optional(),
});
export const ZReleaseNote = z.object({
    _id: z.any().optional(),
    version: z.string().min(1),
    summary: z.string().optional().nullable(),
    releaseDate: ZDateish,
    items: z.array(ZReleaseItem).default([]),
    includedDeployNoteIds: z.array(ZObjectIdString).default([]),
    isPublished: z.boolean().default(true),
});
/* -------------------------------------------------------------------------- */
/*  Publish release (input)                                                   */
/* -------------------------------------------------------------------------- */
export const ZPublishReleaseRequest = z.object({
    version: z.string().min(1),
    title: z.string().nullable().optional(),
    summary: z.string().optional().nullable(),
    deployNoteIds: z.array(ZObjectIdString).min(1),
});
